import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Wrench, Search, CheckCircle2, AlertTriangle } from "lucide-react";

export default function InkTroubleshoot() {
  const [defects, setDefects] = useState([]);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/ink/defects").then(({ data }) => {
      setDefects(data);
      if (data.length) setSelected(data[0]);
    }).catch(() => {});
  }, []);

  const filtered = defects.filter(d =>
    !q || d.name.toLowerCase().includes(q.toLowerCase()) || d.description?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
      <div className="mb-6">
        <div className="text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
          <Link to="/app/ink" className="hover:text-primary">Ink &amp; Print</Link> / Troubleshoot
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mt-2" data-testid="troubleshoot-title">
          Print Troubleshooting AI
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Ranked root causes and corrective actions for the 9 most common print defects. Used as reference by the Copilot when generating customer reports.
        </p>
        <div className="mt-4">
          <Link to="/app/ink/copilot" className="inline-flex items-center gap-2 text-sm text-accent hover:underline font-mono-plex uppercase tracking-widest">
            → Run a full case in the Copilot
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Left: defect list */}
        <div className="lg:col-span-1">
          <div className="border border-border bg-card rounded-sm">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search defects…" className="pl-9 rounded-sm h-9" data-testid="defect-search" />
              </div>
            </div>
            <div className="max-h-[calc(100vh-16rem)] overflow-y-auto">
              {filtered.map((d) => (
                <button
                  key={d.slug}
                  onClick={() => setSelected(d)}
                  className={`w-full text-left px-4 py-3 border-b border-border last:border-b-0 transition-colors ${
                    selected?.slug === d.slug ? "bg-primary/5 border-l-2 border-l-primary" : "hover:bg-secondary/50"
                  }`}
                  data-testid={`defect-item-${d.slug}`}
                >
                  <div className="font-medium text-sm">{d.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{d.description}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right: detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <div className="border border-border bg-card p-8 rounded-sm" data-testid={`defect-detail-${selected.slug}`}>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-sm bg-accent/10 grid place-items-center shrink-0">
                  <Wrench className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight">{selected.name}</h2>
                  <p className="text-muted-foreground mt-2">{selected.description}</p>
                </div>
              </div>

              <div className="mt-8 grid md:grid-cols-2 gap-8">
                <div>
                  <div className="overline text-accent mb-3 inline-flex items-center gap-2">
                    <AlertTriangle className="w-3.5 h-3.5" /> Likely root causes
                  </div>
                  <ul className="space-y-2">
                    {(selected.likely_causes || []).map((c, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <span className="text-[10px] font-mono-plex text-muted-foreground pt-1 shrink-0">0{i+1}</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="overline text-emerald-600 mb-3 inline-flex items-center gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Corrective actions
                  </div>
                  <ul className="space-y-2">
                    {(selected.corrective_actions || []).map((a, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                        <span>{a}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border rounded-sm p-16 text-center bg-card/40">
              <Wrench className="w-8 h-8 mx-auto text-muted-foreground" />
              <div className="font-display text-lg mt-4">Pick a defect</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
